import React, { useState, useEffect, useMemo } from 'react';
import { GenericView, SearchInput } from '../../../../components/common/table';
import ActionButtons from '../../../../components/common/table/ActionButtons';
import FilterDropdown from '../../../../components/common/FilterDropdown';
import TaskForm from '../../../Task/components/TaskForm';
import { useTaskForm } from './hooks/useTaskForm';
import Alert from '../../../../components/Alert/Alert';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import api from '../../../../api';
import { createTask, updateTask } from '../../../Task/api/taskApi';
import { ActivityLogger } from '../../../../utils/activityLogger';
import { useAuth } from '../../../../contexts/AuthProvider';

const TaskTab = ({ lead, onCountChange, isLoading, onTaskChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({
    dateFrom: '',
    dateTo: '',
    priority: '',
    status: '',
    type: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]); // Store all tasks for filtering
  const [taskTypes, setTaskTypes] = useState([]); // Store task types from API
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  
  // ✅ TASK UPDATE PERMISSION: Check if user can view task update date
  const { can } = useAuth();
  const canViewTaskUpdate = can('canviewtaskupdate', 'task');
  
  // Confirmation modal state
  const [deleteTaskModal, setDeleteTaskModal] = useState({ 
    show: false, 
    task: null 
  });

  // Update task count whenever tasks change
  useEffect(() => {
    if (onCountChange) {
      onCountChange(tasks.length);
    }
  }, [tasks, onCountChange]);

  const {
    resetForm,
    populateFormForEdit
  } = useTaskForm(lead);
  
  const [selectedTask, setSelectedTask] = useState(null);

  // const [tasks] = useState([
  //   {
  //     id: 1,
  //     title: 'Follow up on initial meeting',
  //     type: 'Meeting',
  //     file: 'meeting-notes.pdf',
  //     priority: 'high',
  //     createdAt: '2024-03-15T10:30:00',
  //     updatedAt: '2024-03-15T14:20:00',
  //     status: 'to do'
  //   },
  //   {
  //     id: 2,
  //     title: 'Prepare technical documentation',
  //     type: 'Documentation',
  //     file: 'tech-specs.docx',
  //     priority: 'medium',
  //     createdAt: '2024-03-16T09:00:00',
  //     updatedAt: '2024-03-18T11:45:00',
  //     status: 'in progress'
  //   }
  // ]);

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'red',
      medium: 'yellow',
      low: 'green'
    };
    return `badge badge-outline text-${colors[priority] || 'secondary'} me-2`;
  };

  const getStatusBadge = (status) => {
    const colors = {
      'to do': 'secondary',
      'in progress': 'primary',
      'done': 'success'
    };
    return `badge bg-${colors[status] || 'secondary'}-lt me-2`;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleDownloadFile = async (taskId, fileName) => {
    try {
      const response = await api.get(`/crm/lead-tasks/download/${taskId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Log download activity
      await ActivityLogger.logTaskActivity('DOWNLOAD_TASK_FILE', 
        { id: taskId, fileName }, 
        lead, 
        { fileSize: response.data.size }
      );
    } catch (error) {
      console.error('Error downloading file:', error);
      showAlert('danger', 'Failed to download file');
    }
  };

  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => `TASK-${value.toString().padStart(4, '0')}`
    },
    {
      key: 'title',
      label: 'Task Title'
    },
    {
      key: 'type',
      label: 'Type Task'
    },
    {
      key: 'fileName',
      label: 'File',
      render: (value, row) => value ? (
        <a href="#" className="text-decoration-none" onClick={(e) => {
          e.preventDefault();
          handleDownloadFile(row.id, value);
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
            <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"></path>
          </svg>
          {value}
        </a>
      ) : '-'
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span className={getPriorityBadge(value)}>
          {value}
        </span>
      )
    },
    {
      key: 'createdAt',
      label: 'Created At',
      render: (value) => formatDateTime(value)
    },
    // ✅ TASK UPDATE PERMISSION: Only show Updated At column if user has permission
    ...(canViewTaskUpdate ? [{
      key: 'updatedAt',
      label: 'Updated At',
      render: (value) => formatDateTime(value)
    }] : []),
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={getStatusBadge(value)}>
          {value}
        </span>
      )
    }
  ];

  const handleEdit = (task) => {
    populateFormForEdit(task);
    // Transform task data to match main TaskForm's expected format.
    // TaskForm's type options use the task type's original title case as their value
    // ("Follow Up"), so the saved type is passed through untouched — slugifying it here
    // left the Type select with no matching option, i.e. blank on every edit.
    setSelectedTask({
      id: task.id,
      taskTitle: task.title,
      type: task.type || '',
      priority: task.priority,
      status: task.status,
      description: task.description,
      dueDate: task.dueDate ? (typeof task.dueDate === 'string' && task.dueDate.includes('T') 
        ? task.dueDate.split('T')[0] 
        : task.dueDate) : '',
      clientId: lead?.companyId || lead?.company,
      contactId: lead?.contactId || lead?.contact || lead?.pic,
      leadId: lead.id,
      assignedUserId: task.assignedUserId || lead?.assignedUserId,
      fileName: task.fileName,
      file: task.fileName ? { name: task.fileName, isExisting: true } : null
    });
    setIsModalOpen(true);
  };

  const handleDelete = (task) => {
    setDeleteTaskModal({
      show: true,
      task: task
    });
  };

  const handleConfirmDeleteTask = async () => {
    const { task } = deleteTaskModal;
    
    try {
      await api.delete(`/crm/lead-tasks/${task.id}`);
      
      // Log delete activity
      await ActivityLogger.logTaskActivity('DELETE_TASK', task, lead);

      // Remove task from state after successful deletion
      setTasks(prevTasks => prevTasks.filter(t => t.id !== task.id));
      setAllTasks(prevTasks => prevTasks.filter(t => t.id !== task.id));
      onTaskChange?.();
      showAlert('success', 'Task deleted successfully!');
    } catch (err) {
      console.error("Failed to delete task:", err);
      showAlert('danger', 'Failed to delete task. Please try again.');
    }
    
    setDeleteTaskModal({ show: false, task: null });
  };
  

  const handleExportExcel = () => {
    // TODO: Implement Excel export functionality
  };

  const handleExportPDF = () => {
    // TODO: Implement PDF export functionality
  };

  const handleFilterChange = (field, value) => {
    setFilterData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      priority: '',
      status: '',
      type: ''
    });
  };

  const handleFilterApply = () => {
    // Filter logic is now handled in the useMemo below
  };

  const filterFields = [
    {
      name: 'priority',
      label: 'Priority',
      labelField: 'name',
      searchField: 'name'
    },
    {
      name: 'status',
      label: 'Status',
      labelField: 'name',
      searchField: 'name'
    },
    {
      name: 'type',
      label: 'Type',
      labelField: 'name',
      searchField: 'name'
    }
  ];

  const filterOptions = {
    priorityOptions: [
      { id: 'high', name: 'High' },
      { id: 'medium', name: 'Medium' },
      { id: 'low', name: 'Low' }
    ],
    statusOptions: [
      { id: 'to do', name: 'To Do' },
      { id: 'in progress', name: 'In Progress' },
      { id: 'done', name: 'Done' }
    ],
    typeOptions: taskTypes.map(taskType => ({
      id: taskType.taskType.toLowerCase().replace(/\s+/g, '-'),
      name: taskType.taskType
    }))
  };

  const handleOpenModal = () => {
    resetForm();
    setSelectedTask(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
    setSelectedTask(null);
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    // Auto-hide alert after 5 seconds
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const hideAlert = () => {
    setAlert({ show: false, type: '', message: '' });
  };

  // Bridge function to convert main TaskForm data format and delegate to shared task API
  // (uses the same createTask/updateTask as the Task page, which includes email notification)
  const handleTaskSubmitFromMainForm = async (formDataFromMainForm) => {
    try {
      // TaskForm already submits the original title case ("Follow Up"); this only rescues a
      // lowercase-dash value from an older cached bundle, and otherwise leaves it as-is.
      let originalType = formDataFromMainForm.type;
      if (formDataFromMainForm.type && taskTypes.length > 0) {
        const foundType = taskTypes.find(tt =>
          tt.taskType.toLowerCase().replace(/\s+/g, '-') === formDataFromMainForm.type
        );
        if (foundType) originalType = foundType.taskType;
      }

      // Build the payload expected by createTask / updateTask in taskApi.js
      const taskDataForApi = {
        ...formDataFromMainForm,
        type: originalType,
        leadId: lead.id,
        assignedUserId: formDataFromMainForm.assignedUserId || lead?.assignedUserId || lead?.assignedTo?.id || null,
      };

      if (selectedTask && selectedTask.id) {
        await updateTask(selectedTask.id, taskDataForApi);
      } else {
        // createTask handles email notification to the assigned user automatically
        await createTask(taskDataForApi);
      }

      const action = selectedTask ? 'UPDATE_TASK' : 'CREATE_TASK';
      await ActivityLogger.logTaskActivity(action, taskDataForApi, lead);

      showAlert('success', selectedTask ? 'Task updated successfully!' : 'Task created successfully!');

      const res = await api.get(`/crm/lead-tasks?leadId=${lead.id}`);
      setAllTasks(res.data);
      setTasks(res.data);
      // Follow Up History lives on the lead detail response, so it needs its own refetch —
      // otherwise the new task event only appears after a full page reload.
      onTaskChange?.();

      resetForm();
      setSelectedTask(null);
      return true;
    } catch (error) {
      console.error('Error saving task:', error);
      showAlert('danger', 'An error occurred while saving the task. Please try again.');
      return false;
    }
  };

  const addTaskButton = (
    <button className="btn btn-primary" onClick={handleOpenModal}>
      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M12 5l0 14"></path>
        <path d="M5 12l14 0"></path>
      </svg>
      Add New Task
    </button>
  );

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await api.get(`/crm/lead-tasks?leadId=${lead.id}`);
        setAllTasks(res.data); // Store all tasks
        setTasks(res.data); // Initially show all tasks
      } catch (err) {
        console.error("Failed to fetch tasks", err);
      }
    };

    const fetchTaskTypes = async () => {
      try {
        const res = await api.get('/md/task-type');
        setTaskTypes(res.data);
      } catch (err) {
        console.error("Failed to fetch task types", err);
        // Fallback to hardcoded options if API fails
        setTaskTypes([
          { id: 1, taskType: 'Meeting' },
          { id: 2, taskType: 'Documentation' },
          { id: 3, taskType: 'Follow Up' }
        ]);
      }
    };

    const logViewActivity = async () => {
      // Log that user viewed tasks for this lead
      await ActivityLogger.logActivity(
        'VIEW_LEAD_TASKS',
        'Viewed Lead Tasks',
        `Viewed tasks for lead: ${lead.companyName || 'Unknown Company'}`,
        'eye',
        { leadId: lead.id, taskCount: tasks.length }
      );
    };

    fetchTasks();
    fetchTaskTypes();
    logViewActivity();
  }, [lead.id]);

  // Filter tasks based on search query and filter data
  const filteredTasks = useMemo(() => {
    let filtered = [...allTasks];

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task => 
        task.title?.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query) ||
        task.type?.toLowerCase().includes(query) ||
        task.status?.toLowerCase().includes(query) ||
        task.priority?.toLowerCase().includes(query) ||
        `TASK-${task.id?.toString().padStart(4, '0')}`.toLowerCase().includes(query)
      );
    }

    // Apply filter data
    if (filterData.priority) {
      filtered = filtered.filter(task => task.priority === filterData.priority);
    }

    if (filterData.status) {
      filtered = filtered.filter(task => task.status === filterData.status);
    }

    if (filterData.type) {
      // Convert filter type back to original format for comparison
      const originalType = taskTypes.find(tt => 
        tt.taskType.toLowerCase().replace(/\s+/g, '-') === filterData.type
      )?.taskType;
      filtered = filtered.filter(task => task.type === originalType);
    }

    if (filterData.dateFrom) {
      const fromDate = new Date(filterData.dateFrom);
      filtered = filtered.filter(task => {
        const taskDate = new Date(task.createdAt);
        return taskDate >= fromDate;
      });
    }

    if (filterData.dateTo) {
      const toDate = new Date(filterData.dateTo);
      toDate.setHours(23, 59, 59, 999); // Set to end of day
      filtered = filtered.filter(task => {
        const taskDate = new Date(task.createdAt);
        return taskDate <= toDate;
      });
    }

    return filtered;
  }, [allTasks, searchQuery, filterData]);

  // Update displayed tasks when filters change
  useEffect(() => {
    setTasks(filteredTasks);
  }, [filteredTasks]);


  return (
    <>
      {/* Custom Alert Component - Positioned at top like in Prospects */}
      {alert.show && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={hideAlert}
        />
      )}
      <GenericView
        data={tasks}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        checkboxSelection={false}
        showTopControls={true}
        isLoading={isLoading}
        searchComponent={
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search tasks..."
          />
        }
        filterComponent={
          <FilterDropdown
            filterData={filterData}
            onFilterChange={handleFilterChange}
            onReset={handleFilterReset}
            onApply={handleFilterApply}
            filterFields={filterFields}
            options={filterOptions}
          />
        }
        actionButtons={
          <ActionButtons
            onExportExcel={handleExportExcel}
            onExportPDF={handleExportPDF}
            additionalActions={addTaskButton}
            showExport={true}
          />
        }
        emptyStateMessage="No tasks available"
      />

      <TaskForm
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleTaskSubmitFromMainForm}
        initialData={selectedTask ? {
          ...selectedTask,
          clientName: (lead?.companyId || lead?.company)?.toString() || '',
          contactName: (lead?.contactId || lead?.contact || lead?.pic)?.toString() || '',
          projectName: lead.id?.toString() || '',
          dueDate: selectedTask.dueDate ? (typeof selectedTask.dueDate === 'string' && selectedTask.dueDate.includes('T') 
            ? selectedTask.dueDate.split('T')[0] 
            : selectedTask.dueDate) : ''
        } : {
          clientName: (lead?.companyId || lead?.company)?.toString() || '',
          contactName: (lead?.contactId || lead?.contact || lead?.pic)?.toString() || '',
          projectName: lead.id?.toString() || '',
          leadId: lead.id,
          assignedUserId: lead?.assignedUserId || lead?.assignedTo?.id || null
        }}
        disableCompanyField={true}
        hasViewOwnPermission={false}
        hasViewGlobalPermission={true}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteTaskModal.show}
        onClose={() => setDeleteTaskModal({ show: false, task: null })}
        onConfirm={handleConfirmDeleteTask}
        title="Delete Task"
        message="Are you sure you want to delete this? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        modalId="delete-task-modal"
      />

    </>
  );
};

export default TaskTab;